import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';


const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, isAuthenticated, userProfile, loading: authLoading, updatePassword } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Check if this is a password recovery redirect
  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const type = hashParams?.get('type');
    
    if (type === 'recovery') {
      setShowResetPassword(true);
    }
  }, [location]);

  // Redirect if already authenticated based on role
  useEffect(() => {
    if (!authLoading && isAuthenticated && userProfile) {
      if (userProfile?.role === 'super_admin') {
        navigate('/super-admin-dashboard', { replace: true });
      } else if (userProfile?.role === 'practitioner_admin') {
        navigate('/practitioner-dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, userProfile, authLoading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    if (!formData?.email || !formData?.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { data, error: signInError } = await signIn(formData?.email, formData?.password);
      
      if (signInError) {
        if (signInError?.message?.includes('Invalid login credentials')) {
          setError('Email ou mot de passe incorrect');
        } else if (signInError?.message?.includes('Email not confirmed')) {
          setError('Veuillez confirmer votre email');
        } else {
          setError('Erreur de connexion. Veuillez réessayer.');
        }
        setLoading(false);
        return;
      }
      
      // Navigation will happen via useEffect after userProfile loads
    } catch (err) {
      console.error('Login error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();

    if (!forgotPasswordEmail) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    setForgotPasswordLoading(true);
    setError('');

    try {
      // Use supabase.functions.invoke with PUBLIC anon key (no Authorization needed)
      const { data, error: invokeError } = await supabase?.functions?.invoke('send-reset-email', {
        body: { email: forgotPasswordEmail }
      });

      // Log response for debugging
      console.log('send-reset-email response:', data);

      // ALWAYS show success toast (security: never reveal if email exists)
      setForgotPasswordSuccess(true);
    } catch (err) {
      // Only show error if network is truly down (no response)
      console.error('Network error during password reset:', err);
      if (err?.message?.includes('fetch') || err?.message?.includes('network')) {
        setError('Erreur réseau. Veuillez vérifier votre connexion.');
      } else {
        // For any other error, still show success (security)
        setForgotPasswordSuccess(true);
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword?.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setResetPasswordLoading(true);
    setError('');

    try {
      const { error: updateError } = await updatePassword(newPassword);

      if (updateError) {
        throw updateError;
      }

      // Success - redirect to dashboard
      navigate('/login', { replace: true });
      window.location?.reload();
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Chargement...</p>
        </div>
      </div>
    );
  }

  // Reset Password View
  if (showResetPassword) {
    return (
      <>
        <Helmet>
          <title>Nouveau mot de passe - EndoReferral</title>
        </Helmet>
        <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
          
          <div className="w-full max-w-md relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 shadow-2xl">
                <Icon name="Lock" size={36} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Nouveau mot de passe
              </h1>
              <p className="text-gray-400">
                Créez votre nouveau mot de passe
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
              <form onSubmit={handleResetPassword} className="space-y-6">
                {error && (
                  <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                    Nouveau mot de passe
                  </label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e?.target?.value)}
                    placeholder="••••••••"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    disabled={resetPasswordLoading}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                    Confirmer le mot de passe
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    placeholder="••••••••"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    disabled={resetPasswordLoading}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all shadow-xl flex items-center justify-center gap-2"
                  disabled={resetPasswordLoading}
                >
                  {resetPasswordLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Check" size={20} />
                      <span>Enregistrer le mot de passe</span>
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <>
        <Helmet>
          <title>Mot de passe oublié - EndoReferral</title>
        </Helmet>
        <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
          
          <div className="w-full max-w-md relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 shadow-2xl">
                <Icon name="Mail" size={36} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Mot de passe oublié ?
              </h1>
              <p className="text-gray-400">
                Entrez votre email pour réinitialiser votre mot de passe
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
              {forgotPasswordSuccess ? (
                <div className="space-y-6">
                  <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-green-300 font-medium mb-1">Email envoyé !</p>
                      <p className="text-sm text-green-300">
                        Si votre email existe dans notre système, vous recevrez un lien de réinitialisation.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordSuccess(false);
                      setForgotPasswordEmail('');
                    }}
                    className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="ArrowLeft" size={20} />
                    <span>Retour à la connexion</span>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-6">
                  {error && (
                    <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{error}</p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="forgotEmail" className="block text-sm font-medium text-gray-300 mb-2">
                      Adresse e-mail
                    </label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e?.target?.value)}
                      placeholder="votre@email.com"
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                      disabled={forgotPasswordLoading}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all shadow-xl flex items-center justify-center gap-2"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={20} />
                        <span>Envoyer le lien</span>
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setError('');
                    }}
                    className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                    disabled={forgotPasswordLoading}
                  >
                    <Icon name="ArrowLeft" size={20} />
                    <span>Retour à la connexion</span>
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Connexion - EndoReferral</title>
      </Helmet>
      <div className="min-h-screen bg-[#1A1A1E] flex items-center justify-center p-4">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-[#1A1A1E] to-[#1A1A1E]" />
        
        <div className="w-full max-w-md relative z-10">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 shadow-2xl">
              <Icon name="Shield" size={36} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              EndoReferral
            </h1>
            <p className="text-gray-400">
              Connectez-vous pour accéder à votre espace
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-600/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse e-mail
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Mail" size={20} className="text-gray-500" />
                  </div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="votre@email.com"
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Lock" size={20} className="text-gray-500" />
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData?.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading}
                  >
                    <Icon 
                      name={showPassword ? 'EyeOff' : 'Eye'} 
                      size={20} 
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                    />
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-medium transition-all shadow-xl flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Connexion en cours...</span>
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={20} />
                    <span>Se connecter</span>
                  </>
                )}
              </Button>
            </form>

            {/* Forgot Password Link */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                Problème de connexion ?
                <br />
                <a href="mailto:contact@endoreferral.fr" className="text-blue-400 hover:text-blue-300 font-medium">
                  Contactez le support
                </a>
              </p>
            </div>

            {/* Security Badge */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                <Icon name="Shield" size={16} className="text-green-400" />
                <span>Connexion sécurisée - Données médicales protégées</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>© 2026 EndoReferral - Système de référencement endodontique</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;